import { useState, useMemo } from 'react';
import { Pane, Button, Text, Avatar, Badge, Tooltip, IconButton, TickIcon, TimeIcon, DownloadIcon } from 'evergreen-ui';
import { calcMonthlyStats } from '../../../utils/turniStats';
import TurniPdfPreviewModal from '../TurniPdfPreviewModal';
import TurniStatsRecap from '../TurniStatsRecap';
import { useRole } from '../../../contexts/RoleContext';

const GIORNI_SETTIMANA = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export default function VistaSettimanale({
  mockDays,
  selectedWeek,
  setSelectedWeek,
  collaboratorsList,
  turniData,
  managerFerieList,
  reperibilitaData,
  getInitials,
  getBadgeProps,
  handleTimbra
}) {
  const { effectiveIsDirector: isDirector } = useRole();
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const weeks = [];
  for (let i = 0; i < mockDays.length; i += 7) {
    weeks.push(mockDays.slice(i, i + 7));
  }
  const weekDays = weeks[selectedWeek] || [];
  const allDays = mockDays.filter(Boolean);
  const statsOverview = useMemo(() =>
    collaboratorsList.map(user =>
      calcMonthlyStats(allDays, turniData, managerFerieList, user, reperibilitaData)
    ),
    [collaboratorsList, allDays, turniData, managerFerieList, reperibilitaData]
  );

  return (
    <>
    <Pane border="default" borderRadius={4} minWidth={1000}>
      {/* Header Selezione Settimana */}
      <Pane padding={12} display="flex" gap={12} borderBottom="default" alignItems="center">
        {weeks.map((_, i) => (
          <Button key={i} appearance={selectedWeek === i ? 'primary' : 'default'} onClick={() => setSelectedWeek(i)}>
            Settimana {i + 1}
          </Button>
        ))}
        <Pane flex={1} />
        <Button iconBefore={DownloadIcon} onClick={() => setIsPdfModalOpen(true)}>
          Genera PDF
        </Button>
      </Pane>

      {/* Header Tabella */}
      <Pane display="flex" background="tint2" padding={12} borderBottom="default" alignItems="center">
        <Pane width={160}><Text fontWeight="bold">Operatore</Text></Pane>
        {weekDays.map((d, index) => {
          return (
            <Pane flex={1} key={d ? d.date : `empty-${index}`} textAlign="center">
              <Text size={300} fontWeight="bold">{d ? d.date : ''}</Text>
              {d && <Text size={300} color="muted" display="block">{GIORNI_SETTIMANA[index % 7]}</Text>}
            </Pane>
          );
        })}
      </Pane>

      {/* Righe Utenti */}
      {collaboratorsList.map(user => {
        const uName = `${user.nome} ${user.cognome}`.trim();
        return (
          <Pane display="flex" padding={8} borderBottom="muted" key={user.id} alignItems="center">
            <Pane width={160} display="flex" alignItems="center" gap={8} overflow="hidden">
              <Avatar name={getInitials(uName)} size={24} />
              <Text size={300} truncate>{uName}</Text>
            </Pane>

            {weekDays.map((d, index) => {
              if (!d) return <Pane flex={1} key={`empty-${index}`} textAlign="center" background="tint1" />;
              let content = <Text color="muted">-</Text>;

              const ls = managerFerieList.filter(leave => leave.operator_name === uName && leave.start_date <= d.fullDate && leave.end_date >= d.fullDate && leave.status === 'approvata');
              if (ls.length > 0) {
                  let timeStr = "";
                  if (['permesso', 'ferie'].includes((ls[0].leave_type || '').toLowerCase()) && ls[0].start_time && ls[0].start_time.substring(0,5) !== '00:00' && ls[0].end_time) {
                    timeStr = ` ${ls[0].start_time.substring(0,5)}-${ls[0].end_time.substring(0,5)}`;
                  }
                  const badgeInitial = (ls[0].leave_type || 'FER').substring(0,1).toUpperCase();
                  const badgeColor = isDirector ? (badgeInitial === 'M' ? 'red' : badgeInitial === 'P' ? 'yellow' : 'green') : 'green';
                  const badgeText = isDirector ? `${badgeInitial}${timeStr}` : `${(ls[0].leave_type || 'FER').substring(0,3).toUpperCase()}${timeStr}`;
                  content = <Badge color={badgeColor} isSolid>{badgeText}</Badge>;
              } else {
                  const ss = turniData.filter(shift => shift.operator_name === uName && shift.date === d.fullDate);
                  if (ss.length > 0) {
                      content = (
                          <Pane display="flex" flexDirection="column" alignItems="center" gap={4}>
                              {ss.map((s, i) => {
                                  const isAbsence = ['ferie', 'permesso', 'malattia'].includes(s.shift_type);
                                  const bp = getBadgeProps(s.shift_type);

                                  let timeStr = "";
                                  if (isAbsence && s.start_time && s.start_time.substring(0,5) !== '00:00' && s.end_time && ['permesso', 'ferie'].includes(s.shift_type)) {
                                    timeStr = ` ${s.start_time.substring(0,5)}-${s.end_time.substring(0,5)}`;
                                  }

                                  const badgeInitial = isAbsence ? (s.shift_type.substring(0,1).toUpperCase()) : '';
                                  const directorBadgeColor = badgeInitial === 'M' ? 'red' : badgeInitial === 'P' ? 'yellow' : 'green';

                                  let finalColor = bp.color;
                                  let finalIsSolid = !isAbsence;
                                  let displayText = `${s.start_time ? s.start_time.substring(0,5) : '?'} - ${s.end_time ? s.end_time.substring(0,5) : '?'}`;

                                  if (isAbsence && isDirector) {
                                      finalColor = directorBadgeColor;
                                      finalIsSolid = true;
                                      displayText = `${badgeInitial}${timeStr}`;
                                  } else if (isAbsence) {
                                      displayText = `${bp.text.substring(0,3).toUpperCase()}${timeStr}`;
                                      finalIsSolid = true;
                                  }

                                  const noteText = (s.notes && String(s.notes).trim() !== '' && String(s.notes) !== '0') ? s.notes : 'Nessuna nota';

                                  return (
                                    <Tooltip content={noteText} key={s.id}>
                                      <Pane display="flex" alignItems="center" gap={4}>
                                          <Badge color={finalColor} isSolid={finalIsSolid} paddingX={6} style={{ textTransform: 'none', whiteSpace: 'normal', display: 'block', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {displayText} {s.is_overtime ? '(Str.)' : ''}
                                          </Badge>
                                          {s.status === 'timbrato' ? (
                                              <TickIcon color="success" size={12} title="Timbrato" />
                                          ) : (
                                              isDirector && !isAbsence && (
                                                <IconButton
                                                    icon={TimeIcon}
                                                    size="small"
                                                    appearance="minimal"
                                                    disabled={d.fullDate > new Date().toISOString().split('T')[0]}
                                                    onClick={(e) => { e.stopPropagation(); handleTimbra(s.id, d.fullDate); }}
                                                    title="Timbra turno"
                                                />
                                              )
                                          )}
                                      </Pane>
                                    </Tooltip>
                                  )
                              })}
                          </Pane>
                      )
                  }
              }

              const dayReps = (reperibilitaData || []).filter(r => r.operator_name === uName && r.date === d.fullDate);
              return (
                <Pane flex={1} textAlign="center" key={d.date} display="flex" justifyContent="center">
                  <Pane display="flex" flexDirection="column" alignItems="center" gap={2}>
                    {content}
                    {dayReps.map((r, i) => (
                      <Badge key={`rs_${i}`} color="purple" isSolid paddingX={6} style={{ textTransform: 'none', fontSize: 10, whiteSpace: 'normal' }}>
                        {r.period === 'notte' ? '☾' : '☀'} {r.start_time?.substring(0,5)} - {r.end_time?.substring(0,5)}
                      </Badge>
                    ))}
                  </Pane>
                </Pane>
              );
            })}
          </Pane>
        );
      })}

      <TurniStatsRecap statsOverview={statsOverview} />
    </Pane>
    <TurniPdfPreviewModal
      isOpen={isPdfModalOpen}
      onClose={() => setIsPdfModalOpen(false)}
      allDays={allDays}
      collaboratorsList={collaboratorsList}
      turniData={turniData}
      managerFerieList={managerFerieList}
      reperibilitaData={reperibilitaData}
    />
    </>
  );
}
